import { Founder } from "@/components/site/Founder";
import { motion } from "framer-motion";
import { Globe, Instagram } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];

const BrandBlock = ({ name, tag, text, links, tint, i }) => (
  <motion.section
    initial={{ opacity: 0, y: 40 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 1, ease: EASE }}
    className={`border-t border-brand-border py-24 lg:py-32 ${tint}`}
    data-testid={`founder-brand-${i}`}
  >
    <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 px-6 md:grid-cols-12 lg:px-12">
      <div className="md:col-span-4">
        <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-gold">{tag}</p>
        <h2 className="mt-4 font-display text-4xl font-medium text-brand-ink md:text-5xl">{name}</h2>
        {links && (
          <div className="mt-6 flex flex-wrap gap-3">
            {links.map((l) => (
              <a
                key={l.label}
                href={l.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={l.testid}
                className="inline-flex items-center gap-2 rounded-full border border-brand-gold px-5 py-2 text-[10px] font-medium uppercase tracking-[0.2em] text-brand-maroon transition-colors duration-300 hover:bg-brand-gold hover:text-brand-ivory"
              >
                <l.icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                {l.label}
              </a>
            ))}
          </div>
        )}
      </div>
      <p className="max-w-2xl text-base font-light leading-relaxed text-brand-ink/70 md:col-span-7 md:col-start-6 md:text-lg">
        {text}
      </p>
    </div>
  </motion.section>
);

export default function FounderPage() {
  return (
    <main data-testid="founder-page" className="pt-28 lg:pt-32">
      <Founder />
      <BrandBlock
        i="tinkred"
        name="Tinkred"
        tag="Her Other Labour of Love"
        tint="bg-brand-cream/40"
        text="Tinkred reflects Jagriti Mehta's belief that learning should nurture both the mind and the heart. Drawing on her experience in early childhood education, curriculum development and teacher training, it brings together practical learning, emotional awareness and wellbeing. Through courses, educator resources and thoughtful guidance, Tinkred supports learners, teachers, parents and professionals in building confidence, resilience and empathy — carrying the founder's care for people into the way they learn, teach and grow."
        links={[
          { label: "www.tinkred.net", href: "https://www.tinkred.net", icon: Globe, testid: "tinkred-website-link" },
          { label: "@tinkredhq", href: "https://instagram.com/tinkredhq", icon: Instagram, testid: "tinkred-instagram-link" },
        ]}
      />
      <BrandBlock
        i="laxwedeepan"
        name="Laxwedeepan"
        tag="Born of Bhakti"
        tint=""
        text="Laxwedeepan grew from Jagriti Mehta's personal connection with peace, contentment and bhakti. Its heart lies in shuddhata — the intention of purity; shilp — the care of skilled hands; and bhakti — the devotion that gives creation meaning. Through ghee, clay toys, handcrafted frames, matchsticks and flower oils, the brand honours natural materials, cultural traditions and everyday rituals. Each creation carries her wish to bring simplicity, beauty and reverence into the home, prepared with the care of an offering."
      />
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 1, ease: EASE }}
        className="border-t border-brand-border bg-brand-cream/40 py-24 text-center lg:py-32"
        data-testid="founder-two-brands"
      >
        <div className="mx-auto max-w-3xl px-6">
          <p className="text-[11px] font-medium uppercase tracking-[0.4em] text-brand-gold">Two Brands, One Founder's Heart</p>
          <p className="mt-8 font-display text-2xl font-medium leading-snug text-brand-ink md:text-3xl">
            Tinkred creates space for knowledge, emotional wellbeing and meaningful
            learning; Laxwedeepan creates space for purity, craftsmanship and
            spiritual connection. At the heart of both is her belief that intention
            shapes what we offer the world — whether it is a lesson that helps
            someone grow or a handcrafted object that becomes part of a cherished
            ritual. Together, the brands carry her hope for lives enriched by
            understanding, kindness, creativity and peace.
          </p>
          <p className="mt-10 font-display text-2xl italic text-brand-maroon">— Jagriti Mehta</p>
        </div>
      </motion.section>
    </main>
  );
}
